import { DelegationBar } from '@/pages/tenantManagement/DelegationBar';
import { TenantSettingsBar } from '@/pages/tenantManagement/TenantSettingsBar';
import { useTenantSettingsBar } from '@/pages/tenantSettings/TenantSettingsBarContext';
import { Disclosure, DisclosureButton, DisclosurePanel, MenuItem } from '@headlessui/react';
import { FC, Fragment, HTMLAttributes, Suspense, useEffect } from 'react';
import { IconType } from 'react-icons';
import { FiFileText, FiLogOut, FiMenu, FiSettings, FiSliders, FiUser, FiX } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { merge } from '../../helpers/Utility';
import {
  isAdmin,
  Role,
  selectIsDelegatedManager,
  selectRequiresActingLocation,
  useAuthenticationService
} from '../../services/authentication/authentication-service';
import { pageType } from '../../services/authentication/routes/AuthenticatedRoute';
import { Flags } from '../../services/backstage/backstage.model';
import { Feature } from '../../services/backstage/Feature';
import { useEmployeeAvatar } from '../../services/employee/EmployeeAvatarProvider';
import { handleError } from '../../services/gql/config/URQLProvider';
import { nonSuspenseQueryContext } from '../../services/gql/config/useRetainedQueryState';
import { useGetMeQuery } from '../../services/gql/graphql.generated';
import { Divider } from '../shared/Divider';
import { ConfirmedLink } from '../shared/link/ConfirmedLink';
import { Dropdown, DropdownOrigin } from '../shared/menu/Dropdown';
import { TimeshiftAvatar } from '../shared/TimeshiftAvatar';
import { TimeshiftLogo } from '../shared/TimeshiftLogo';
import { HeaderMobileSubmenuGroup, HeaderSubmenuLink } from './HeaderSubmenuLink';
import strings from './page.translations';
import { PageSizer, SizerMode } from './PageSizer';

interface MainHeaderProps extends HTMLAttributes<HTMLDivElement> {
  fullWidth?: boolean;
}

const pages: pageType[] = [
  { name: strings.common.home, to: '/', rolesRequired: [Role.Admin, Role.Manager, Role.Staff] },
  {
    name: strings.nav.pageNames.team,
    to: '/team',
    variant: 'submenu',
    rolesRequired: [Role.Admin, Role.Manager],
    children: [
      { name: strings.nav.staffList, to: '/team', rolesRequired: [Role.Admin, Role.Manager] },
      {
        name: strings.nav.pageNames.calendar,
        to: '/team/calendar',
        rolesRequired: [Role.Admin, Role.Manager],
        feature: 'roster'
      }
    ]
  },
  { name: strings.nav.pageNames.shifts, to: '/shifts', rolesRequired: [Role.Admin, Role.Manager], feature: 'clockIn' },
  { name: strings.nav.pageNames.roster, to: '/rosters', rolesRequired: [Role.Admin, Role.Manager], feature: 'roster' },
  {
    name: strings.nav.pageNames.templates,
    to: '/templates',
    rolesRequired: [Role.Admin, Role.Manager],
    feature: 'roster'
  },
  { name: strings.nav.pageNames.calendar, to: '/calendar', rolesRequired: [Role.Admin], feature: 'roster' },
  { name: strings.nav.pageNames.calendar, to: '/ess/my-calendar', rolesRequired: [Role.Staff], feature: 'roster' },
  { name: strings.nav.pageNames.admin, to: '/admin', rolesRequired: [Role.Admin] },
  {
    name: strings.nav.pageNames.tenantManagement,
    to: '/',
    rolesRequired: [Role.SuperAdmin],
    feature: 'tenantManagement'
  },
  {
    name: strings.nav.pageNames.tenantSettings,
    to: '/admin-tenant-settings',
    rolesRequired: [Role.Admin],
    onlyDelegatedSuperAdmin: true
  }
];

// Routes accessible to Admin before a location has been selected
const ADMIN_PRE_LOCATION_PATHS = new Set(['/', '/admin']);

interface NavigationLinkProps {
  page: pageType;
  isMobile?: boolean;
}

export const NavigationLink: FC<NavigationLinkProps> = ({ page, isMobile }) => {
  const location = useLocation();
  const isActive =
    page.to === '/'
      ? location.pathname === '/'
      : location.pathname === page.to || location.pathname.startsWith(`${page.to}/`);

  return (
    <ConfirmedLink
      key={`${page.name}-${page.to}`}
      to={page.to}
      className={`border-transparent h-full hover:border-primary-std dark:hover:border-white inline-flex items-center font-medium ${
        isActive ? 'border-primary-500 text-primary-std' : 'text-secondary-std'
      } ${isMobile ? 'pl-3 pr-4 py-4 border-l-4 text-base' : 'px-1 pt-1 border-b-2 text-sm'}`}
    >
      {page.name}
    </ConfirmedLink>
  );
};

interface DropdownLinkProps extends React.PropsWithChildren {
  to: string;
  Icon: IconType;
}

export const DropdownLink: React.FC<DropdownLinkProps> = ({ children, to, Icon }) => {
  return (
    <MenuItem>
      <ConfirmedLink to={to} className="space-x-2 flex py-3 px-3 border-b last:border-b-0">
        <Icon className="w-6 h-6" />
        {children}
      </ConfirmedLink>
    </MenuItem>
  );
};

const AvatarMenuSkeleton = () => (
  <div className="px-4 py-3 space-y-2 animate-pulse">
    <div className="h-5 w-32 bg-secondary-std rounded" />
    <div className="h-4 w-48 bg-secondary-std rounded" />
  </div>
);

export const MainHeader: FC<MainHeaderProps> = ({ className, ...props }) => {
  const [role, user, isDelegatedSuperAdmin, isDelegatedManager, requiresActingLocation] = useAuthenticationService(
    state => [
      state.role,
      state.user,
      state.isDelegatedSuperAdmin,
      selectIsDelegatedManager(state),
      selectRequiresActingLocation(state)
    ]
  );
  const { show, tenantId, tenantName, close } = useTenantSettingsBar();
  const { employeeAvatarRefreshToken, lastUpdatedEmployeeId } = useEmployeeAvatar();
  const [{ data, error }, refetchMe] = useGetMeQuery({ context: nonSuspenseQueryContext });
  if (error) handleError(error);
  const name = strings.employeeName(data?.myEmployee?.firstName ?? '', data?.myEmployee?.lastName ?? '');
  const myUserId = data?.myEmployee?.identityId ?? '';

  useEffect(() => {
    if (employeeAvatarRefreshToken > 0 && lastUpdatedEmployeeId === myUserId) {
      refetchMe({ requestPolicy: 'network-only' });
    }
  }, [employeeAvatarRefreshToken, lastUpdatedEmployeeId, myUserId, refetchMe]);

  const lockedPage = (lockedStyle: string, page: pageType) => {
    return (
      <div data-tip={strings.nav.lockedFeature} className={lockedStyle}>
        {page.name}
      </div>
    );
  };

  const canShowPage = (page: pageType) => {
    if (page.onlyDelegatedSuperAdmin) {
      return isDelegatedSuperAdmin === true;
    }

    if (isDelegatedSuperAdmin) {
      return false;
    }

    if (!page.rolesRequired || !page.rolesRequired.includes(role!)) return false;

    if (requiresActingLocation && !isDelegatedManager) {
      return ADMIN_PRE_LOCATION_PATHS.has(page.to);
    }

    return true;
  };

  const renderHeaderPage = (page: pageType, isMobile?: boolean) => {
    if (page.locked) {
      return lockedPage(
        `border-transparent ${
          isMobile
            ? 'text-secondary-std hover:bg-secondary-std block pl-3 pr-4 py-4 border-l-4 text-base'
            : 'cursor-default text-secondary-std inline-flex items-center px-1 pt-1 border-b-2 text-sm'
        } font-medium`,
        page
      );
    }

    if (page.variant === 'submenu') {
      return (
        <Feature name={page.feature as Flags}>
          {isMobile ? <HeaderMobileSubmenuGroup page={page} /> : <HeaderSubmenuLink page={page} />}
        </Feature>
      );
    }

    return <Feature name={page.feature as Flags}>{<NavigationLink page={page} isMobile={isMobile} />}</Feature>;
  };

  return (
    <Disclosure as="nav">
      {({ open }) => (
        <div>
          <div
            {...props}
            className={merge('w-full bg-primary-std border-primary-std border-b print:hidden', className)}
          >
            {open && <DisclosureButton className="absolute w-full h-20 z-20"></DisclosureButton>}
            <PageSizer className="flex items-stretch relative" mode={SizerMode.HEADER}>
              <div className="flex items-center lg:mr-4 lg:hidden z-10">
                {/* Mobile menu button */}
                <DisclosureButton className="inline-flex items-center justify-center p-2 rounded-md text-primary-std hover:text-primary-std hover:bg-secondary-std focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500">
                  <span className="sr-only">{strings.nav.mobileOpenMenu}</span>
                  {open ? (
                    <FiX className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <FiMenu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>
              <div className={'flex items-center justify-center py-2 w-full lg:w-auto'}>
                <Link to="/">
                  <TimeshiftLogo className="h-7" />
                </Link>
              </div>

              <div className="hidden lg:ml-6 lg:flex lg:space-x-8 whitespace-nowrap -my-4">
                {pages.filter(canShowPage).map(page => (
                  <Fragment key={page.to}>{renderHeaderPage(page)}</Fragment>
                ))}
              </div>

              <div className={'z-[10000] flex gap-5 items-center ml-auto justify-end'}>
                <Dropdown
                  origin={DropdownOrigin.TOP_RIGHT}
                  button={<TimeshiftAvatar name={name} size={40} src={data?.myEmployee?.avatarUrl} />}
                >
                  <Suspense fallback={<AvatarMenuSkeleton />}>
                    <div className="px-4 py-3 border-b">
                      <h2 className="text-xl font-medium">{name}</h2>
                      <p className="text-secondary-std">{data?.myEmployee?.email}</p>
                    </div>
                    <Feature name="respect">
                      {role !== Role.SuperAdmin && myUserId && (
                        <DropdownLink
                          to={role === Role.Staff ? '/ess/my-profile' : `/team-member/${myUserId}`}
                          Icon={FiUser}
                        >
                          <span>{strings.nav.myProfile}</span>
                        </DropdownLink>
                      )}
                    </Feature>
                    <Feature name="gdpr">
                      {role !== Role.SuperAdmin && (
                        <DropdownLink to={'/my-data'} Icon={FiFileText}>
                          <span>{strings.nav.myData}</span>
                        </DropdownLink>
                      )}
                    </Feature>
                    <Feature name="debug">
                      <DropdownLink to={'/scribbles'} Icon={FiSliders}>
                        <span>{strings.nav.uiTest}</span>
                      </DropdownLink>
                    </Feature>
                    <Feature name="devices">
                      {isAdmin(role) && (
                        <DropdownLink to={'/manage-organisation'} Icon={FiSettings}>
                          <span>{strings.nav.manageOrg}</span>
                        </DropdownLink>
                      )}
                    </Feature>
                    {role === Role.Admin && (
                      <DropdownLink to={'/admin-tenant-settings'} Icon={FiSettings}>
                        <span>{strings.nav.tenantSettings}</span>
                      </DropdownLink>
                    )}
                    <DropdownLink to={'/logout'} Icon={FiLogOut}>
                      <span>{strings.nav.logout}</span>
                    </DropdownLink>
                  </Suspense>
                </Dropdown>
              </div>
            </PageSizer>
            <PageSizer className="py-0 px-0" mode={SizerMode.FULL}>
              <DelegationBar />
            </PageSizer>
            <PageSizer className="py-0" mode={SizerMode.FULL}>
              {show && tenantId && <TenantSettingsBar tenantId={tenantId} tenantName={tenantName} onClose={close} />}
            </PageSizer>
          </div>

          <DisclosurePanel className="lg:hidden absolute h-[0] w-full z-[10000]">
            <div className="bg-primary-std space-y-px z-20">
              {pages.filter(canShowPage).map(page => (
                <Fragment key={page.to}>
                  <div>{renderHeaderPage(page, true)}</div>
                  <Divider />
                </Fragment>
              ))}
            </div>
            <DisclosureButton className="absolute bg-black opacity-50 w-full h-screen z-10"></DisclosureButton>
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  );
};
